/**
 * AdCreativeVideoSection — "AI video" panel inside the item detail dialog
 * (Creatives tab). Rendered only for video-template items.
 *
 * States (mock: .design/ad-creative-video/mockups/video-section-states.html):
 *   idle      — model + duration selects, estimated-cost caption, Generate
 *   running   — per-segment status chips + keyframe thumbnails, controls disabled
 *   completed — <video controls poster> player, duration badge, validation chips,
 *               Regenerate menu (plan / keyframes / videos / all)
 *   error     — error alert with segment context, Regenerate menu available
 *
 * Task progress itself lives in the global task sidebar (implementation-runtime
 * UX contract: no duplicate inline task trail) — this panel only mirrors the
 * per-segment state persisted on item.videoPlan.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { StyledTextField } from '@modules/ui-components';
import type {
  AdCreativeItem,
  AdCreativeVideoPlan,
  AdCreativeVideoSegmentPlan,
} from '@segmently/schema-models/ad-creative';
import type { AiAssetExperiment } from '@segmently/schema-models/content-plan';
import { useTaskTransition } from '@modules/background-tasks';
import { AD_CREATIVE } from '../selectors';
import type { AdCreativeApiClient } from '../services/AdCreativeApiClient';
import { adCreativeTaskItemId, isAdCreativeVideoTask } from '../services/adCreativeTaskScope';

/**
 * Video model options with ESTIMATED per-second rates (plan §5.1 / risk R2 —
 * official Veo preview pricing unpublished; the caption always says
 * "estimated"). Kept local: ai-core's videoModelPricing reads process.env,
 * which is unavailable in the browser bundle.
 */
const VIDEO_MODEL_OPTIONS = [
  { id: 'veo-3.1-fast-generate-preview', label: 'Veo 3.1 Fast (Preview)', estimatedUsdPerSecond: 0.15 },
  { id: 'veo-3.1-generate-preview', label: 'Veo 3.1 (Preview, Best Quality)', estimatedUsdPerSecond: 0.35 },
  { id: 'veo-3.1-lite-generate-preview', label: 'Veo 3.1 Lite (Preview, Cheap Drafts)', estimatedUsdPerSecond: 0.05 },
] as const;

const DURATION_OPTIONS = [6, 10, 15, 20, 24, 30] as const;

/** Creative types whose templates carry a primary video slot (MVP: reel). */
const VIDEO_CREATIVE_TYPES = new Set(['reel', 'single_video', 'story_video', 'in_stream_video']);

/** Slow safety net while the plan runs (FR0-PRE-003); the background-tasks
 * transition subscription is the primary refresh signal. */
const SAFETY_REFRESH_INTERVAL_MS = 60_000;

export function isVideoCreativeItem(item: AdCreativeItem): boolean {
  return VIDEO_CREATIVE_TYPES.has(String(item.creativeType)) || Boolean(item.videoPlan);
}

interface AdCreativeVideoSectionProps {
  projectId: string;
  item: AdCreativeItem;
  client: AdCreativeApiClient;
  /** Re-fetches the item document and pushes the fresh copy back into the dialog. */
  onRefreshItem: () => Promise<void> | void;
}

type RegenerateMode = 'plan' | 'keyframes' | 'videos' | 'all';

export function AdCreativeVideoSection({ projectId, item, client, onRefreshItem }: AdCreativeVideoSectionProps): React.ReactElement | null {
  const [model, setModel] = useState<string>(VIDEO_MODEL_OPTIONS[0].id);
  const [durationSec, setDurationSec] = useState<number>(10);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // Latest callback in a ref so the subscriptions never re-arm on parent renders.
  const onRefreshItemRef = useRef(onRefreshItem);
  onRefreshItemRef.current = onRefreshItem;

  const videoPlan = item.videoPlan as AdCreativeVideoPlan | undefined;
  const running = videoPlan?.status === 'running' || starting;

  const videoExperiment = useMemo(() => findVideoExperiment(item, videoPlan?.slotKey), [item, videoPlan?.slotKey]);
  const completed = videoPlan?.status === 'completed' && Boolean(videoExperiment?.generatedUrl);
  const failed = videoPlan?.status === 'error';

  const selectedModel = VIDEO_MODEL_OPTIONS.find((option) => option.id === model) ?? VIDEO_MODEL_OPTIONS[0];
  const costEstimate = (durationSec * selectedModel.estimatedUsdPerSecond).toFixed(2);

  // Progress arrives through the app-level background-tasks subscription
  // (FR0-I1); the old 10s poll is gone. Transitions of this item's video task
  // refresh the item; a slow safety interval covers missed writes.
  useTaskTransition({
    filter: (task) => isAdCreativeVideoTask(task) && adCreativeTaskItemId(task) === item.id,
    onTransition: () => {
      void onRefreshItemRef.current();
    },
  });

  useEffect(() => {
    if (videoPlan?.status !== 'running') return undefined;
    const interval = window.setInterval(() => {
      void onRefreshItemRef.current();
    }, SAFETY_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [videoPlan?.status]);

  const startGeneration = async (regenerate?: RegenerateMode) => {
    setStarting(true);
    setStartError(null);
    setMenuAnchor(null);
    try {
      const response = await client.generateItemVideo(projectId, item.id, {
        model: regenerate ? videoPlan?.model ?? model : model,
        targetDurationSec: regenerate ? videoPlan?.targetDurationSec ?? durationSec : durationSec,
        regenerate,
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to start video generation');
      }
      await onRefreshItem();
    } catch (error) {
      setStartError(error instanceof Error ? error.message : String(error));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Box data-testid={AD_CREATIVE.videoSection} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>AI video</Typography>

        {!completed && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <StyledTextField
              select
              label="Video model"
              value={model}
              data-testid={AD_CREATIVE.videoModel}
              onChange={(value) => setModel(String(value))}
              disabled={running}
              size="small"
              sx={{ minWidth: 260 }}
            >
              {VIDEO_MODEL_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
              ))}
            </StyledTextField>
            <StyledTextField
              select
              label="Duration"
              value={String(durationSec)}
              data-testid={AD_CREATIVE.videoDuration}
              onChange={(value) => setDurationSec(Number(value))}
              disabled={running}
              size="small"
              sx={{ minWidth: 120 }}
            >
              {DURATION_OPTIONS.map((option) => (
                <MenuItem key={option} value={String(option)}>{option} s</MenuItem>
              ))}
            </StyledTextField>
            <Button
              data-testid={AD_CREATIVE.videoGenerate}
              variant="contained"
              size="small"
              onClick={() => void startGeneration()}
              disabled={running}
              startIcon={running ? <CircularProgress size={14} /> : undefined}
            >
              {running ? 'Generating…' : 'Generate video'}
            </Button>
          </Stack>
        )}

        {!completed && (
          <Typography variant="caption" color="text.secondary" data-testid={AD_CREATIVE.videoCostCaption}>
            {`≈ $${costEstimate} per run · estimated — official Veo preview pricing not yet published`}
          </Typography>
        )}

        {startError && <Alert severity="error">{startError}</Alert>}

        {videoPlan && videoPlan.segments?.length > 0 && !completed && (
          <SegmentRow item={item} videoPlan={videoPlan} />
        )}

        {running && videoPlan?.status === 'running' && (
          <Typography variant="caption" color="text.secondary">
            Video segments render one at a time. Progress also appears in the task sidebar.
          </Typography>
        )}

        {failed && (
          <Alert severity="error" data-testid={AD_CREATIVE.videoError}>
            {videoPlan?.error || 'Video generation failed.'}
            {' '}
            Keyframes are preserved — use “Regenerate videos”.
          </Alert>
        )}

        {completed && videoExperiment && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <Box
              component="video"
              controls
              data-testid={AD_CREATIVE.videoPlayer}
              poster={videoExperiment.posterUrl}
              src={videoExperiment.generatedUrl}
              sx={{ width: 236, maxWidth: '100%', borderRadius: 1, bgcolor: 'grey.100' }}
            />
            <Stack spacing={1.5} sx={{ flex: 1, minWidth: 240 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  variant="outlined"
                  data-testid={AD_CREATIVE.videoDurationBadge}
                  label={`${(videoExperiment.durationSec ?? videoPlan?.totalDurationSec ?? 0).toFixed(1)} s`}
                />
                <Chip size="small" variant="outlined" label={`${videoPlan?.resolution ?? '720p'} · ${videoPlan?.aspectRatio ?? '9:16'}`} />
                <Chip size="small" variant="outlined" label={modelLabel(videoPlan?.model)} />
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <ValidationChip id="duration_bounds" passed={durationWithinBounds(videoPlan, videoExperiment)} />
                <ValidationChip id="thumbnail_selected" passed={Boolean(videoExperiment.posterUrl)} />
              </Stack>
              {videoPlan && <SegmentRow item={item} videoPlan={videoPlan} />}
            </Stack>
          </Stack>
        )}

        {(completed || failed) && (
          <Box>
            <Button
              data-testid={AD_CREATIVE.videoRegenerate}
              variant="outlined"
              size="small"
              endIcon={<ArrowDropDownIcon />}
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              disabled={running}
            >
              Regenerate
            </Button>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem data-testid={AD_CREATIVE.videoRegenerateOption('plan')} onClick={() => void startGeneration('plan')}>Replan storyboard</MenuItem>
              <MenuItem data-testid={AD_CREATIVE.videoRegenerateOption('keyframes')} onClick={() => void startGeneration('keyframes')}>Regenerate keyframes</MenuItem>
              <MenuItem data-testid={AD_CREATIVE.videoRegenerateOption('videos')} onClick={() => void startGeneration('videos')}>Regenerate videos (reuse keyframes)</MenuItem>
              <MenuItem data-testid={AD_CREATIVE.videoRegenerateOption('all')} onClick={() => void startGeneration('all')}>Regenerate all</MenuItem>
            </Menu>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

function SegmentRow({ item, videoPlan }: { item: AdCreativeItem; videoPlan: AdCreativeVideoPlan }): React.ReactElement {
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
      {videoPlan.segments.map((segment) => (
        <Stack key={segment.index} spacing={0.75} alignItems="flex-start">
          <KeyframeThumb item={item} segment={segment} />
          <Chip
            size="small"
            data-testid={AD_CREATIVE.videoSegmentChip(segment.index)}
            color={segmentChipColor(segment.status)}
            label={segmentChipLabel(segment)}
          />
        </Stack>
      ))}
    </Stack>
  );
}

function KeyframeThumb({ item, segment }: { item: AdCreativeItem; segment: AdCreativeVideoSegmentPlan }): React.ReactElement {
  const keyframeUrl = findExperimentUrl(item, segment.keyframeSlotKey);
  if (keyframeUrl) {
    return (
      <Box
        component="img"
        src={keyframeUrl}
        alt={`Segment ${segment.index + 1} keyframe`}
        sx={{ width: 56, height: 100, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'grey.100' }}
      />
    );
  }
  return (
    <Box sx={{ width: 56, height: 100, borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="caption" color="text.disabled">keyframe</Typography>
    </Box>
  );
}

function ValidationChip({ id, passed }: { id: string; passed: boolean }): React.ReactElement {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={passed ? 'success' : 'error'}
      data-testid={AD_CREATIVE.videoValidationChip(id)}
      label={`${passed ? '✓' : '✕'} ${id}`}
    />
  );
}

function segmentChipColor(status: AdCreativeVideoSegmentPlan['status']): 'default' | 'success' | 'info' | 'error' {
  if (status === 'completed') return 'success';
  if (status === 'generating') return 'info';
  if (status === 'error') return 'error';
  return 'default';
}

function segmentChipLabel(segment: AdCreativeVideoSegmentPlan): string {
  const base = `Segment ${segment.index + 1} · ${segment.durationSec}s`;
  if (segment.status === 'completed') return `✓ ${base}`;
  if (segment.status === 'error') return `${base} · failed`;
  if (segment.status === 'generating') return `${base} · generating`;
  if (segment.status === 'keyframe_ready') return `${base} · keyframe ready`;
  return `${base} · pending`;
}

function modelLabel(modelId?: string): string {
  return VIDEO_MODEL_OPTIONS.find((option) => option.id === modelId)?.label
    ?? modelId
    ?? VIDEO_MODEL_OPTIONS[0].label;
}

function durationWithinBounds(videoPlan?: AdCreativeVideoPlan, experiment?: AiAssetExperiment): boolean {
  const target = Number(videoPlan?.targetDurationSec);
  const measured = Number(experiment?.durationSec ?? videoPlan?.totalDurationSec);
  if (!Number.isFinite(target) || !Number.isFinite(measured) || target <= 0 || measured <= 0) return false;
  return Math.abs(measured - target) / target <= 0.2;
}

function findVideoExperiment(item: AdCreativeItem, slotKey?: string): AiAssetExperiment | undefined {
  const aiAssets = item.aiAssets as Record<string, { selectedExperimentId?: string; experiments?: Record<string, AiAssetExperiment> }> | undefined;
  if (!aiAssets) return undefined;
  const slots = slotKey && aiAssets[slotKey] ? [aiAssets[slotKey]] : Object.values(aiAssets);
  for (const slot of slots) {
    const selected = slot.selectedExperimentId ? slot.experiments?.[slot.selectedExperimentId] : undefined;
    if (selected?.assetType === 'video') return selected;
    const anyVideo = Object.values(slot.experiments ?? {}).find((experiment) => experiment.assetType === 'video');
    if (anyVideo) return anyVideo;
  }
  return undefined;
}

function findExperimentUrl(item: AdCreativeItem, slotKey: string): string | undefined {
  const aiAssets = item.aiAssets as Record<string, { selectedExperimentId?: string; experiments?: Record<string, AiAssetExperiment> }> | undefined;
  const slot = aiAssets?.[slotKey];
  if (!slot?.experiments) return undefined;
  const selected = slot.selectedExperimentId ? slot.experiments[slot.selectedExperimentId] : Object.values(slot.experiments)[0];
  return selected?.status === 'completed' ? selected.generatedUrl : undefined;
}
